// Ported ~verbatim from nenda-autoplayer's src/lib/state/playbackCircuitBreakerMachine.ts.
// Pure XState, zero framework dependencies — no changes needed for this port.
// See docs/memory (or the plan at implementation time) for the "surgical port" rationale:
// this is the mechanism that turns a silent stuck-video into a reported, retried failure
// instead of a permanently black screen with healthy-looking telemetry.
import { setup, assign, sendParent } from "xstate";

type SUCCESS = "SUCCESS";
type FAIL = "FAIL";
type PROBE = "PROBE";

const CIRCUIT_BREAKER_THRESHOLD = 3; // number of failures before opening

// monotonic exponential backoff with small jitter
const calcBackoff = (
  fails: number,
  prev: number, // last cooldownMs
  base = 1000,
  cap = 60_000,
  jitterPct = 0.1 // ±10% jitter
) => {
  const target = base * 2 ** (fails - 1);
  const jitter = 1 + (Math.random() * 2 - 1) * jitterPct; // 0.9..1.1
  const sampled = Math.round(target * jitter);
  const nonDecreasing = Math.max(prev || 0, sampled);
  return Math.min(cap, nonDecreasing);
};

const ts = () => new Date().toISOString();

type Context = { fails: number; cooldownMs: number };

export const playbackCircuitBreakerMachine = setup({
  types: {} as {
    context: Context;
    events: { type: SUCCESS } | { type: FAIL } | { type: PROBE };
  },
  actions: {
    logOpened: ({ context }) => {
      console.log(
        `[${ts()}] Playback API unhealthy. fails=${context.fails} cooldown=${context.cooldownMs}ms `
      );
    },
    logClosed: () => {
      console.log(`[${ts()}] Playback API healthy again.`);
    },
    logHalfOpen: () => {
      console.log(`[${ts()}] Playback API half-opened, probing...`);
    },
    notifyParentProbe: sendParent(() => ({
      type: "PLAYBACK_CIRCUITBREAKER_HALF_OPENED",
    })),
    notifyParentOpen: sendParent(() => ({
      type: "PLAYBACK_CIRCUITBREAKER_OPENED",
    })),
    notifyParentClosed: sendParent(() => ({
      type: "PLAYBACK_CIRCUITBREAKER_CLOSED",
    })),
  },
  delays: {
    PROBE_DELAY: ({ context }: { context: Context }) => context.cooldownMs,
  },
}).createMachine({
  id: "playbackCircuitBreaker",
  initial: "closed",
  context: { fails: 0, cooldownMs: 0 },
  states: {
    closed: {
      on: {
        FAIL: [
          {
            guard: ({ context }) =>
              context.fails + 1 >= CIRCUIT_BREAKER_THRESHOLD,
            actions: assign(({ context }) => {
              const fails = context.fails + 1;
              return {
                fails,
                cooldownMs: calcBackoff(fails, context.cooldownMs),
              };
            }),
            target: "open",
          },
          { actions: assign(({ context }) => ({ fails: context.fails + 1 })) },
        ],
        SUCCESS: { actions: assign({ fails: 0 }) },
      },
    },
    open: {
      entry: ["logOpened", "notifyParentOpen"],
      after: {
        PROBE_DELAY: {
          target: "halfOpen",
        },
      },
      on: {
        // allow immediate recovery on a single success
        SUCCESS: {
          actions: ["logClosed", "notifyParentClosed", assign({ fails: 0 })],
          target: "closed",
        },
      },
    },
    halfOpen: {
      entry: ["logHalfOpen", "notifyParentProbe"],
      on: {
        SUCCESS: {
          actions: ["logClosed", "notifyParentClosed", assign({ fails: 0 })],
          target: "closed",
        },
        FAIL: {
          actions: assign(({ context }) => {
            const fails = context.fails + 1;
            return {
              fails,
              cooldownMs: calcBackoff(fails, context.cooldownMs),
            };
          }),
          target: "open",
        },
      },
    },
  },
});
